import { computed, ref } from "vue";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import incomeService, { type IncomeCategory } from "@/services/income";

type IncomeCategoryForm = {
  code: string;
  name: string;
  description: string;
};

const createInitialForm = (): IncomeCategoryForm => ({
  code: "",
  name: "",
  description: "",
});

export function useIncomeCategory() {
  const { t } = useI18n();
  const categories = ref<IncomeCategory[]>([]);
  const searchQuery = ref("");
  const loading = ref(false);
  const saving = ref(false);
  const selectedCategory = ref<IncomeCategory | null>(null);
  const form = ref<IncomeCategoryForm>(createInitialForm());

  const filteredCategories = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return categories.value;
    return categories.value.filter((category) =>
      [category.code, category.name, category.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  });

  const fetchCategories = async () => {
    loading.value = true;
    try {
      categories.value = await incomeService.getIncomeCategories();
    } catch (error) {
      console.error("Error fetching income categories:", error);
      Swal.fire({ icon: "error", title: t("income_category_page.failed_to_load"), text: t("income_category_page.unable_to_load_income_categories") });
    } finally {
      loading.value = false;
    }
  };

  const generateCode = () => {
    const normalizedCodes = new Set(categories.value.map((item) => String(item.code || "").trim().toUpperCase()));
    let nextNumber = normalizedCodes.size + 1;
    while (normalizedCodes.has(`INC-${String(nextNumber).padStart(3, "0")}`)) {
      nextNumber += 1;
    }
    form.value.code = `INC-${String(nextNumber).padStart(3, "0")}`;
  };

  const openCreateModal = () => {
    selectedCategory.value = null;
    form.value = createInitialForm();
    generateCode();
  };

  const openEditModal = (category: IncomeCategory) => {
    selectedCategory.value = category;
    form.value = {
      code: category.code || "",
      name: category.name || "",
      description: category.description || "",
    };
  };

  const updateField = (field: keyof IncomeCategoryForm, value: string) => {
    form.value[field] = value;
  };

  const hideModal = () => {
    const modalElement = document.getElementById("incomeCategoryModal");
    if (!modalElement) return;

    const BootstrapModal = Modal as any;
    const modalInstance =
      BootstrapModal.getInstance?.(modalElement) ||
      BootstrapModal.getOrCreateInstance?.(modalElement) ||
      new Modal(modalElement);

    modalInstance.hide();
  };

  const submitCategory = async () => {
    if (!form.value.code.trim() || !form.value.name.trim()) {
      await Swal.fire({ icon: "warning", title: t("income_category_page.missing_fields"), text: t("income_category_page.code_and_name_required") });
      return;
    }

    saving.value = true;
    try {
      if (selectedCategory.value?.id) {
        await incomeService.updateIncomeCategory(selectedCategory.value.id, { ...form.value, status: 1 });
      } else {
        await incomeService.createIncomeCategory({ ...form.value, status: 1 });
      }
      await fetchCategories();
      hideModal();
      await Swal.fire({
        icon: "success",
        title: selectedCategory.value ? t("income_category_page.category_updated") : t("income_category_page.category_created"),
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error saving income category:", error);
      Swal.fire({ icon: "error", title: t("income_category_page.save_failed"), text: t("income_category_page.failed_to_save_income_category") });
    } finally {
      saving.value = false;
    }
  };

  const deleteCategory = async (category: IncomeCategory) => {
    if (!category.id) return;
    const result = await Swal.fire({
      title: t("income_category_page.delete_category_title"),
      text: t("income_category_page.delete_category_text", { name: category.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("income_category_page.confirm_delete"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;

    try {
      await incomeService.deleteIncomeCategory(category.id);
      await fetchCategories();
      Swal.fire({ icon: "success", title: t("income_category_page.deleted"), text: t("income_category_page.income_category_deleted_successfully"), timer: 1400, showConfirmButton: false });
    } catch (error) {
      console.error("Error deleting income category:", error);
      Swal.fire({ icon: "error", title: t("income_category_page.delete_failed"), text: t("income_category_page.unable_to_delete_income_category") });
    }
  };

  return {
    categories: filteredCategories,
    searchQuery,
    loading,
    saving,
    form,
    isEditMode: computed(() => !!selectedCategory.value),
    fetchCategories,
    openCreateModal,
    openEditModal,
    updateField,
    generateCode,
    submitCategory,
    deleteCategory,
  };
}
